import type { OrderDto } from '../api/models/OrderDto';
import type { CalculatedProductionPlanDto } from '../api/models/CalculatedProductionPlanDto';
import type { CampaignData } from '../api/models/CampaignData';

export type SequenceSnapshotCampaign = {
  campaignId?: string;
  campaignUuid?: string;
  materialId?: string;
  sequenceNumber?: number;
};

export type SequenceGanttSlot =
  | { kind: 'special'; orderUuids: Set<string>; label: string }
  | { kind: 'campaign'; orderUuids: Set<string>; label: string };

/** Parse production schedule sequenceSnapshot JSON; null if missing or invalid. */
export function parseSequenceSnapshot(json: string | undefined | null): SequenceSnapshotCampaign[] | null {
  if (json == null || !String(json).trim()) {
    return null;
  }
  try {
    const root = JSON.parse(String(json)) as { campaigns?: SequenceSnapshotCampaign[] };
    const list = root.campaigns;
    if (!Array.isArray(list)) {
      return null;
    }
    return [...list].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));
  } catch {
    return null;
  }
}

export function campaignShortHash(campaignUuid?: string, campaignId?: string): string {
  if (campaignUuid) {
    const hex = campaignUuid.replace(/-/g, '');
    return hex.slice(0, 8).toLowerCase();
  }
  const alnum = (campaignId ?? '').replace(/[^a-zA-Z0-9]/g, '');
  if (alnum.length > 0) {
    return alnum.slice(0, 8).toLowerCase();
  }
  return '—';
}

function normUuid(u: string | undefined | null): string | undefined {
  if (u == null || !String(u).trim()) {
    return undefined;
  }
  return String(u).trim().toLowerCase();
}

/** Orders belonging to one snapshot row (by campaign UUID preferred, else business campaignId, else sequence number). */
export function orderUuidsForSnapshotRow(row: SequenceSnapshotCampaign, orders: OrderDto[]): Set<string> {
  const out = new Set<string>();
  const rowCampUuid = normUuid(row.campaignUuid);
  const rowCampId = row.campaignId?.trim();
  for (const o of orders) {
    const ou = normUuid(o.orderUuid);
    if (!ou) {
      continue;
    }
    if (rowCampUuid && normUuid(o.campaignUuid) === rowCampUuid) {
      out.add(ou);
      continue;
    }
    const oCampId = o.campaignId != null ? String(o.campaignId).trim() : '';
    if (rowCampId && oCampId === rowCampId) {
      out.add(ou);
    }
  }
  if (out.size === 0 && row.sequenceNumber != null) {
    const sn = row.sequenceNumber;
    for (const o of orders) {
      const ou = normUuid(o.orderUuid);
      if (!ou) {
        continue;
      }
      if (o.campaignSequenceNumber === sn) {
        out.add(ou);
      }
    }
  }
  return out;
}

/** Campaign from the API/schedule to a snapshot row (material name & quantity for the dropdown). */
function findCampaignHintForRow(
  row: SequenceSnapshotCampaign,
  hints: readonly CampaignData[]
): CampaignData | undefined {
  if (!hints.length) {
    return undefined;
  }
  const rowMat = normUuid(row.materialId);
  const rowCu = normUuid(row.campaignUuid);
  const rowCi = row.campaignId?.trim();
  const rowSn = row.sequenceNumber;

  if (rowCu) {
    const byUuid = hints.find((h) => normUuid(h.campaignId) === rowCu);
    if (byUuid) {
      return byUuid;
    }
  }
  if (rowCi) {
    const byCid = hints.find((h) => (h.campaignId != null ? String(h.campaignId).trim() : '') === rowCi);
    if (byCid) {
      return byCid;
    }
  }
  if (rowSn != null && rowMat) {
    const bySeqMat = hints.find(
      (h) => h.sequenceNumber === rowSn && normUuid(h.materialId) === rowMat
    );
    if (bySeqMat) {
      return bySeqMat;
    }
  }
  if (rowSn != null) {
    const bySeq = hints.find((h) => h.sequenceNumber === rowSn);
    if (bySeq) {
      return bySeq;
    }
  }
  return undefined;
}

function quantityFromHint(hint: CampaignData | undefined): number | undefined {
  if (!hint) {
    return undefined;
  }
  const tb = hint.totalBakedQuantity;
  const dq = hint.desiredQuantity;
  if (tb != null && tb > 0) {
    return tb;
  }
  if (dq != null && dq > 0) {
    return dq;
  }
  if (tb != null) {
    return tb;
  }
  if (dq != null) {
    return dq;
  }
  return undefined;
}

export function buildSequenceGanttSlots(
  snapshot: SequenceSnapshotCampaign[] | null,
  plan: CalculatedProductionPlanDto | null,
  followUpOrderUuids: string[] | undefined,
  labels: { special: (n: number) => string },
  campaignHints?: readonly CampaignData[]
): SequenceGanttSlot[] {
  const orders = plan?.orders ?? [];
  const slots: SequenceGanttSlot[] = [];

  const fu = (followUpOrderUuids ?? []).map((u) => normUuid(u)).filter(Boolean) as string[];
  if (fu.length > 0) {
    const orderSet = new Set<string>();
    for (const id of fu) {
      if (orders.some((o) => normUuid(o.orderUuid) === id)) {
        orderSet.add(id);
      }
    }
    if (orderSet.size > 0) {
      slots.push({
        kind: 'special',
        orderUuids: orderSet,
        label: labels.special(fu.length)
      });
    }
  }

  if (snapshot) {
    for (const row of snapshot) {
      if (!row.materialId && !row.campaignUuid && !row.campaignId) {
        continue;
      }
      const orderUuids = orderUuidsForSnapshotRow(row, orders);
      const hint = findCampaignHintForRow(row, campaignHints ?? []);

      const material = orders.find(
        (o) =>
          orderUuids.has(normUuid(o.orderUuid)!) && o.materialName
      );
      const materialName =
        material?.materialName ??
        hint?.materialName ??
        orders.find((o) => normUuid(o.materialUuid) === normUuid(row.materialId))?.materialName ??
        '—';

      const qtyFromOrder = [...orderUuids]
        .map((id) => orders.find((o) => normUuid(o.orderUuid) === id))
        .find(Boolean)?.quantity;
      const qty = qtyFromOrder ?? quantityFromHint(hint);
      const qtyStr = qty != null ? String(qty) : '—';

      let hash = campaignShortHash(row.campaignUuid, row.campaignId);
      if (hash === '—') {
        hash = campaignShortHash(undefined, hint?.campaignId ?? row.campaignId);
      }

      slots.push({
        kind: 'campaign',
        orderUuids,
        label: `${materialName}, ${hash}, ${qtyStr}`
      });
    }
  }

  return slots;
}

export type SequenceFilterSelection = 'all' | number;

/** Cumulative: first `prefixCount` slots (1-based count of slots from the start). */
export function visibleOrderUuidsForPrefix(slots: SequenceGanttSlot[], prefixCount: number): Set<string> {
  const out = new Set<string>();
  const n = Math.max(0, Math.min(prefixCount, slots.length));
  for (let i = 0; i < n; i++) {
    slots[i].orderUuids.forEach((u) => out.add(u));
  }
  return out;
}

export function allOrderUuidsInPlan(plan: CalculatedProductionPlanDto | null): Set<string> {
  const out = new Set<string>();
  for (const o of plan?.orders ?? []) {
    const u = normUuid(o.orderUuid);
    if (u) {
      out.add(u);
    }
  }
  return out;
}
